import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, PieChart, Banknote, AlertCircle, ChevronRight, Tag, Star, ArrowRight, Bell } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { apiCall, default as API_CONFIG } from '../config/api';
import { collectActionableTickers, aggregateTickerActivity, actionAccent } from './reportHelpers';
import { actionBadge } from './reportHelpers';
import { useWatchlistInsights } from '../hooks/useWatchlist';

const Dashboard = () => {
  const navigate = useNavigate();
  const [portfolioData, setPortfolioData] = useState(null);
  const [recentAnalysis, setRecentAnalysis] = useState([]);
  const [tickerActivity, setTickerActivity] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch portfolio data (stocks from Schwab) plus recent reports.
      // 14 reports gives the watchlist ~2 weeks of trading days to aggregate
      // ticker activity over; the recent-analysis panel still only shows
      // the top 5.
      const [stockData, cryptoData, historyData, recentReports] = await Promise.all([
        apiCall(API_CONFIG.endpoints.portfolio).catch(() => ({ holdings: [] })),
        apiCall(API_CONFIG.endpoints.cryptoPortfolio).catch(() => ({ totalValue: 0, holdings: [] })),
        apiCall(API_CONFIG.endpoints.portfolioHistory).catch(() => []),
        apiCall(API_CONFIG.endpoints.reportsWithCount(14)).catch(() => [])
      ]);

      // Get cash balance from Schwab account
      const cashValue = stockData.cashBalance || 0;

      // Calculate stock totals (holdings value, excluding cash)
      const stockValue = (stockData.holdings || []).reduce((sum, h) => sum + (h.marketValue || 0), 0);

      // Crypto total
      const cryptoValue = cryptoData.totalValue || 0;

      // Total portfolio value (stocks + crypto + cash)
      const totalValue = stockValue + cryptoValue + cashValue;

      // Calculate percentages
      const stockPercent = totalValue > 0 ? ((stockValue / totalValue) * 100).toFixed(1) : 0;
      const cryptoPercent = totalValue > 0 ? ((cryptoValue / totalValue) * 100).toFixed(1) : 0;
      const cashPercent = totalValue > 0 ? ((cashValue / totalValue) * 100).toFixed(1) : 0;

      // Process history data for 7-day performance chart
      let chartData = [];
      let dayChange = 0;
      let dayChangePercent = 0;

      if (Array.isArray(historyData) && historyData.length > 0) {
        // Sort by date and take last 7 days
        const sortedHistory = [...historyData]
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(-7);

        chartData = sortedHistory.map(item => ({
          date: format(new Date(item.date), 'MM/dd'),
          value: item.totalValue || 0
        }));

        // Calculate day change from last two entries
        if (sortedHistory.length >= 2) {
          const today = sortedHistory[sortedHistory.length - 1];
          const yesterday = sortedHistory[sortedHistory.length - 2];
          dayChange = (today.totalValue || 0) - (yesterday.totalValue || 0);
          dayChangePercent = yesterday.totalValue > 0
            ? ((dayChange / yesterday.totalValue) * 100).toFixed(2)
            : 0;
        }
      }

      setPortfolioData({
        totalValue,
        cryptoValue,
        stockValue,
        cashValue,
        dayChange,
        dayChangePercent,
        cryptoPercent,
        stockPercent,
        cashPercent
      });
      setPerformanceData(chartData);

      // Map V2 daily report shape (structuredOutput.{summary,callsToAction,...})
      // into the lightweight card shape this list renders. Pull the top
      // actionable tickers inline ("BUY NVDA, SELL TSLA") instead of an
      // opaque action count -- the list is short and the tickers are the
      // payoff.
      const reportArray = Array.isArray(recentReports) ? recentReports : [];
      setRecentAnalysis(
        reportArray.slice(0, 5).map((r) => ({
          id: r.id,
          date: r.reportDate ? new Date(r.reportDate) : new Date(r.generatedAt || Date.now()),
          summary: (r.structuredOutput?.summary) || 'Daily report',
          tickers: collectActionableTickers(r).slice(0, 3),
          totalCalls: (r.structuredOutput?.callsToAction || []).length,
          alertCount: (r.structuredOutput?.portfolioAlerts || []).length,
        }))
      );

      // Roll up ticker activity across the full window for the watchlist widget.
      setTickerActivity(aggregateTickerActivity(reportArray));
      setLoading(false);
    } catch (err) {
      setError('Failed to load dashboard data');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <AlertCircle size={24} />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Your investment overview powered by Russ's insights</p>
      </div>

      {/* Stats Grid */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Total Portfolio Value</div>
          <div className="stat-value">${portfolioData.totalValue.toLocaleString()}</div>
          <div className={`stat-change ${portfolioData.dayChange >= 0 ? 'positive' : 'negative'}`}>
            {portfolioData.dayChange >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            ${Math.abs(portfolioData.dayChange).toLocaleString()} ({portfolioData.dayChangePercent}%) today
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Cryptocurrency</div>
          <div className="stat-value">${portfolioData.cryptoValue.toLocaleString()}</div>
          <div className="stat-change">
            <PieChart size={16} />
            {portfolioData.cryptoPercent}% of portfolio
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Stocks (Schwab)</div>
          <div className="stat-value">${portfolioData.stockValue.toLocaleString()}</div>
          <div className="stat-change">
            <PieChart size={16} />
            {portfolioData.stockPercent}% of portfolio
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Cash</div>
          <div className="stat-value">${portfolioData.cashValue.toLocaleString()}</div>
          <div className="stat-change">
            <Banknote size={16} />
            {portfolioData.cashPercent}% of portfolio
          </div>
        </div>
      </div>

      {/* Portfolio Performance Chart */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h2>7-Day Performance</h2>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={performanceData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="date" 
              stroke="#64748b"
              style={{ fontSize: '0.875rem' }}
            />
            <YAxis
              stroke="#64748b"
              style={{ fontSize: '0.875rem' }}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              // Tight domain so daily 1-2% swings are visible. Default
              // [0, auto] anchored to zero made a $600k portfolio's $10k
              // daily change a 1.5% sliver. Pad the visible range with 5%
              // on each side of the actual min/max for a comfortable
              // baseline without amplifying noise.
              domain={chartYDomain(performanceData)}
            />
            <Tooltip 
              formatter={(value) => [`$${value.toLocaleString()}`, 'Portfolio Value']}
              contentStyle={{
                backgroundColor: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                color: 'var(--text-primary)',
              }}
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="var(--primary-color)"
              strokeWidth={2}
              dot={{ fill: 'var(--primary-color)', r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* User-curated watchlist with conviction-change alerts */}
      <MyWatchlistPanel navigate={navigate} />

      {/* "What has Russ been talking about" -- aggregated ticker activity */}
      <TopTickersWidget activity={tickerActivity} navigate={navigate} />

      {/* Recent AI Analysis */}
      <div className="card">
        <div className="card-header">
          <h2>Recent AI Analysis</h2>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/analysis')}
          >
            View All
          </button>
        </div>

        {recentAnalysis.map((analysis) => (
          <button
            key={analysis.id}
            onClick={() => navigate(`/analysis/${encodeURIComponent(analysis.id)}`)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '1rem',
              borderBottom: '1px solid var(--border-color)',
              background: 'transparent',
              border: 'none',
              borderBottomStyle: 'solid',
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  color: 'var(--text-primary)',
                  marginBottom: '0.5rem',
                  marginTop: 0,
                  fontSize: '0.9375rem',
                  lineHeight: '1.5',
                }}>
                  {analysis.summary}
                </p>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  alignItems: 'center',
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)',
                }}>
                  <span>{format(analysis.date, 'MMM dd, yyyy')}</span>
                  {analysis.tickers.map((t) => (
                    <span key={t.ticker} className={`badge ${t.cssBadge}`}>
                      {t.action} {t.ticker}
                    </span>
                  ))}
                  {analysis.totalCalls > analysis.tickers.length && (
                    <span className="badge badge-info">
                      +{analysis.totalCalls - analysis.tickers.length} more
                    </span>
                  )}
                  {analysis.alertCount > 0 && (
                    <span className="badge badge-warning">
                      {analysis.alertCount} alert{analysis.alertCount === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight size={18} style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: '2px' }} />
            </div>
          </button>
        ))}

        {recentAnalysis.length === 0 && (
          <div className="empty-state">
            <p>No recent reports available</p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Watchlist widget. Aggregates ticker activity across the recent-N report
 * window and surfaces the top N tickers by mention count -- a quick "what's
 * Russ been talking about" view. Each tile shows the dominant action with
 * its color (BUY green, SELL red, HOLD amber), the ticker, and a small
 * footer with mention count + last-seen distance.
 *
 * Clicking a tile jumps to the most recent report mentioning that ticker
 * -- the easiest deep-link without inventing a "filter Analysis by ticker"
 * concept yet.
 */
const TopTickersWidget = ({ activity, navigate }) => {
  if (!activity || activity.length === 0) return null;

  const tiles = activity.slice(0, 8);
  return (
    <div className="card">
      <div className="card-header">
        <h2>
          <Tag size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.375rem' }} />
          Top tickers
        </h2>
        <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
          most-mentioned across recent reports ({tiles.length} of {activity.length})
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: '0.625rem',
      }}>
        {tiles.map((t) => {
          const targetReportId = t.reportIds[0]; // newest-first
          return (
            <button
              key={t.ticker}
              type="button"
              onClick={() => navigate(`/analysis/${encodeURIComponent(targetReportId)}`)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.375rem',
                padding: '0.625rem 0.75rem',
                border: '1px solid var(--border-color)',
                borderLeft: `4px solid ${actionAccent(t.dominantAction)}`,
                borderRadius: '6px',
                background: 'var(--bg-primary)',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              }}
              className="watchlist-tile"
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--primary-color)' }}>
                  {t.ticker}
                </span>
                <span className={`badge ${t.dominantBadge}`} style={{ fontSize: '0.6875rem' }}>
                  {t.dominantAction}
                </span>
              </div>
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
                flexWrap: 'wrap',
              }}>
                <span>
                  {t.mentions} mention{t.mentions === 1 ? '' : 's'}
                </span>
                {t.lastSeen && (
                  <span>
                    {formatDistanceToNowStrict(t.lastSeen, { addSuffix: true })}
                  </span>
                )}
                {t.avgPriority != null && (
                  <span>P{t.avgPriority.toFixed(0)}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <style>{`
        .watchlist-tile:hover {
          border-color: var(--primary-color) !important;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.08);
        }
      `}</style>
    </div>
  );
};

/**
 * User-curated watchlist panel. Backed by GET /api/watchlist/insights:
 *   - The "Recent changes" section flags conviction shifts on watched
 *     tickers (BUY -> SELL, High -> Low, priority jumps, first-action).
 *   - The full pinned list shows the latest action for each entry.
 *
 * Hidden when the user hasn't pinned anything yet (no point taking up
 * dashboard space with an empty card -- user will discover the feature
 * via the star icon in the Mentions browser instead).
 */
const MyWatchlistPanel = ({ navigate }) => {
  const { insights, loading, error } = useWatchlistInsights();

  if (loading || error) return null;
  const entries = insights?.entries || [];
  const changes = insights?.changes || [];
  if (entries.length === 0) return null;

  return (
    <div className="card">
      <div className="card-header">
        <h2>
          <Star size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.375rem', color: 'var(--warning-color)' }} fill="var(--warning-color)" />
          My Watchlist
        </h2>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/mentions')}
          style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}
        >
          Browse all
        </button>
      </div>

      {/* Recent changes -- only render when there are any. */}
      {changes.length > 0 && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem',
          background: 'var(--severity-warn-bg)',
          borderLeft: '4px solid var(--severity-warn-border)',
          borderRadius: '4px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <Bell size={14} />
            Recent changes ({changes.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {changes.slice(0, 5).map((c, i) => (
              <ChangeRow key={`${c.kind}-${c.key}-${i}`} change={c} navigate={navigate} />
            ))}
          </div>
        </div>
      )}

      {/* Pinned entries with their latest action overlay. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '0.5rem',
      }}>
        {entries.map((e) => (
          <EntryTile key={`${e.kind}-${e.key}`} entry={e} navigate={navigate} />
        ))}
      </div>
    </div>
  );
};

const EntryTile = ({ entry, navigate }) => {
  const isTicker = entry.kind === 'ticker';
  const target = isTicker
    ? `/mentions/tickers/${encodeURIComponent(entry.key)}`
    : `/mentions/themes/${encodeURIComponent(entry.key)}`;
  const accent = entry.latestAction ? actionAccent(entry.latestAction) : 'var(--border-color)';

  return (
    <button
      type="button"
      onClick={() => navigate(target)}
      className="watchlist-tile"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.375rem',
        padding: '0.625rem 0.75rem',
        border: '1px solid var(--border-color)',
        borderLeft: `4px solid ${accent}`,
        borderRadius: '6px',
        background: 'var(--bg-primary)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
        <span style={{ fontWeight: 700, fontSize: isTicker ? '1rem' : '0.875rem', color: 'var(--primary-color)' }}>
          {isTicker ? entry.key : (entry.label || entry.key)}
        </span>
        {entry.latestAction && (
          <span className={`badge ${actionBadge(entry.latestAction)}`} style={{ fontSize: '0.6875rem' }}>
            {entry.latestAction}
          </span>
        )}
      </div>
      {entry.latestReportDate && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          {entry.latestConfidence && <>{entry.latestConfidence} · </>}
          {format(new Date(entry.latestReportDate), 'MMM dd')}
        </div>
      )}
      {!entry.latestAction && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
          No recent action
        </div>
      )}
    </button>
  );
};

const ChangeRow = ({ change, navigate }) => {
  const isTicker = change.kind === 'ticker';
  const target = isTicker
    ? `/mentions/tickers/${encodeURIComponent(change.key)}`
    : `/mentions/themes/${encodeURIComponent(change.key)}`;

  let descriptor;
  if (change.changeType === 'new') {
    descriptor = (
      <>
        New action <span className={`badge ${actionBadge(change.currentAction)}`}>{change.currentAction}</span>
        {change.currentConfidence && <span style={{ marginLeft: '0.375rem', color: 'var(--text-secondary)' }}>({change.currentConfidence})</span>}
      </>
    );
  } else if (change.changeType === 'action') {
    descriptor = (
      <>
        <span className={`badge ${actionBadge(change.previousAction)}`}>{change.previousAction}</span>
        <ArrowRight size={12} style={{ verticalAlign: 'middle', margin: '0 0.25rem', color: 'var(--text-secondary)' }} />
        <span className={`badge ${actionBadge(change.currentAction)}`}>{change.currentAction}</span>
      </>
    );
  } else if (change.changeType === 'confidence') {
    descriptor = (
      <>
        Conviction <strong>{change.previousConfidence || '?'}</strong>
        <ArrowRight size={12} style={{ verticalAlign: 'middle', margin: '0 0.25rem', color: 'var(--text-secondary)' }} />
        <strong>{change.currentConfidence || '?'}</strong>
      </>
    );
  } else if (change.changeType === 'priority') {
    descriptor = (
      <>
        Priority <strong>P{change.previousPriority}</strong>
        <ArrowRight size={12} style={{ verticalAlign: 'middle', margin: '0 0.25rem', color: 'var(--text-secondary)' }} />
        <strong>P{change.currentPriority}</strong>
      </>
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigate(target)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.375rem 0.625rem',
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '4px',
        cursor: 'pointer',
        textAlign: 'left',
        fontSize: '0.875rem',
      }}
    >
      <strong style={{ color: 'var(--primary-color)', minWidth: 60 }}>{change.key}</strong>
      <span style={{ flex: 1, minWidth: 0 }}>{descriptor}</span>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
        {format(new Date(change.currentReportDate), 'MMM dd')}
      </span>
    </button>
  );
};

/**
 * Compute a tight [min, max] domain for the 7-day chart's Y axis. Default
 * Recharts behavior anchors to zero, which makes a $600k portfolio's $10k
 * daily move a barely-perceptible sliver. We pad the actual data range by
 * 5% on each side -- enough headroom to avoid clipping the line at the
 * card edges, while keeping the trend visually meaningful.
 *
 * Returns the literal "auto" for both bounds when there's no data so
 * Recharts handles the empty-state placeholder.
 */
const chartYDomain = (data) => {
  if (!Array.isArray(data) || data.length === 0) return ['auto', 'auto'];
  const values = data.map((d) => d?.value ?? 0).filter((v) => Number.isFinite(v));
  if (values.length === 0) return ['auto', 'auto'];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(max - min, 1); // guard against single-point series
  const pad = range * 0.05;
  return [min - pad, max + pad];
};

export default Dashboard;
